import * as moment from 'moment';

/**
 * Building blocks for a Google-style month grid: whole weeks of cells, all-day events drawn as
 * bars across the days they cover, and a few appointment lines per day.
 */

export interface MonthCell {
  date: Date;
  inMonth: boolean;
}

export interface AllDayEvent {
  id: string;
  start: Date;
  end: Date;
  [key: string]: any;
}

export interface BarSegment {
  event: AllDayEvent;
  lane: number;
  /** The title shows on the first day of each week's piece of the bar. */
  label: boolean;
  /** Days from this cell to the end of this week's piece; the title spans them. */
  span: number;
  /** The bar runs on into the cell before / after this one. */
  continuesBefore: boolean;
  continuesAfter: boolean;
}

export interface PreviewLine {
  time: string;
  title: string;
  color: string;
  inactive: boolean;
  /** Confirmed with the patient. */
  confirmed: boolean;
  /** the booking itself, for its hover */
  appointment: any;
  /** the booking's full details, added by the calendar */
  tooltip?: string;
}

export interface DayPreview {
  lines: PreviewLine[];
  more: number;
}

/** The weeks (Sunday first) that hold the month, with the days of the months either side. */
export function monthCells(month: Date): MonthCell[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay());
  const total = Math.ceil((first.getDay() + last.getDate()) / 7) * 7;
  return Array.from({ length: total }, (_, i) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return { date, inMonth: date.getMonth() === month.getMonth() };
  });
}

/** `weeks` whole weeks (Sunday first) from the week holding `date`; every day counts as shown. */
export function weekCells(date: Date, weeks: number): MonthCell[] {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay());
  return Array.from({ length: weeks * 7 }, (_, i) => (
    { date: new Date(start.getFullYear(), start.getMonth(), start.getDate() + i), inMonth: true }));
}

/**
 * For each cell, the bar pieces in it, indexed by lane (a free lane is undefined, so an event
 * keeps its height across its days). Lanes are worked out per week, longer events first.
 */
export function allDayBars(events: AllDayEvent[], cellDates: Date[]): (BarSegment | undefined)[][] {
  const result: (BarSegment | undefined)[][] = cellDates.map(() => []);
  const day = (date: Date) => moment(date).startOf('day');

  for (let w = 0; w < cellDates.length; w += 7) {
    const week = cellDates.slice(w, w + 7).map(day);
    const weekStart = week[0];
    const weekEnd = week[week.length - 1];
    const inWeek = events
      .filter(e => !day(e.end).isBefore(weekStart) && !day(e.start).isAfter(weekEnd))
      .sort((a, b) => day(a.start).diff(day(b.start)) || day(b.end).diff(day(b.start)) - day(a.end).diff(day(a.start)));

    const taken: boolean[][] = week.map(() => []);
    for (const event of inWeek) {
      const from = Math.max(0, day(event.start).diff(weekStart, 'days'));
      const to = Math.min(week.length - 1, day(event.end).diff(weekStart, 'days'));
      let lane = 0;
      while (week.slice(from, to + 1).some((_, i) => taken[from + i][lane])) lane++;
      for (let i = from; i <= to; i++) {
        taken[i][lane] = true;
        result[w + i][lane] = {
          event,
          lane,
          label: i === from,
          span: to - i + 1,
          // Within the week the bar runs on cell to cell; across Saturday or Sunday it carries on
          // into the next or previous week's row.
          continuesBefore: i > from || day(event.start).isBefore(weekStart),
          continuesAfter: i < to || day(event.end).isAfter(weekEnd),
        };
      }
    }
  }
  return result.map(lanes => Array.from({ length: lanes.length }, (_, i) => lanes[i]));
}

/**
 * Joins events that are really one: the same name (ignoring case and spaces) and the same times,
 * on following or overlapping days, as when leave is entered one day at a time. Each result
 * keeps the ids it was made from in `ids`.
 */
export function combineEvents(events: AllDayEvent[]): AllDayEvent[] {
  const key = (e: AllDayEvent) =>
    [String(e['title'] ?? '').trim().toLowerCase(), e['isAllDay'] ? 'all-day' : `${e['startTime'] ?? ''}-${e['endTime'] ?? ''}`].join('|');
  const sorted = [...events].sort((a, b) => key(a).localeCompare(key(b)) || moment(a.start).diff(moment(b.start)));
  const out: AllDayEvent[] = [];
  for (const e of sorted) {
    const prev = out[out.length - 1];
    if (prev && key(prev) === key(e) && !moment(e.start).startOf('day').isAfter(moment(prev.end).startOf('day').add(1, 'day'))) {
      if (moment(e.end).isAfter(prev.end)) prev.end = e.end;
      prev['ids'].push(e.id);
    } else {
      out.push({ ...e, ids: [e.id] });
    }
  }
  return out.sort((a, b) => moment(a.start).diff(moment(b.start)));
}

/**
 * A day's timed appointments as short lines in the `rows` the cell has free: all of them when
 * they fit, otherwise as many as fit with the last row kept for "+n more".
 */
export function dayPreview(appointments: any[], rows: number): DayPreview {
  const timed = (appointments || [])
    .filter(a => !a?.isAllDay)
    .sort((a, b) => String(a.timeSlot ?? '').localeCompare(String(b.timeSlot ?? '')));
  const shown = timed.length <= rows ? timed.length : Math.max(0, rows - 1);
  const lines = timed.slice(0, shown).map(a => ({
    time: String(a.timeSlot ?? '').slice(0, 5),
    title: a.Patient ? [a.Patient.surname, a.Patient.name].filter(Boolean).join(', ') : (a.reason || a.appointmentType || ''),
    color: a.typeDetails?.backgroundColor || '#009688',
    inactive: a.isCancelled === true || a.didNotAttend === true,
    confirmed: !!a.confirmedAt && !!a.Patient,
    appointment: a,
  }));
  return { lines, more: timed.length - lines.length };
}
